import React from "react";
import {
  useAuth,
  useOrganization,
  useOrganizationList,
} from "@clerk/clerk-react";

function orgInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export function WorkspaceSwitcher({
  refresh,
}: {
  refresh: () => Promise<void>;
}) {
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const { organization } = useOrganization();
  const { getToken } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const activate = React.useCallback(
    async (clerkOrgId: string | null) => {
      if (!setActive) return;
      await setActive({ organization: clerkOrgId });
      await getToken({ skipCache: true });
      await refresh();
    },
    [setActive, getToken, refresh],
  );

  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  React.useEffect(() => {
    if (!isLoaded || !setActive) return;
    const mems = userMemberships?.data ?? [];
    if (
      mems.length === 1 &&
      (organization?.id ?? null) !== mems[0].organization.id
    ) {
      void activate(mems[0].organization.id);
    }
  }, [isLoaded, setActive, userMemberships?.data, organization?.id, activate]);

  React.useEffect(() => {
    const activeId = organization?.id ?? null;
    if (!isLoaded || !activeId) return;
    const mems = userMemberships?.data ?? [];
    if (!mems.some((m) => m.organization.id === activeId)) {
      void userMemberships?.revalidate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, organization?.id]);

  if (!isLoaded) return null;

  async function choose(clerkOrgId: string | null) {
    if (!setActive) return;
    setBusy(true);
    try {
      await activate(clerkOrgId);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  const items = (userMemberships?.data ?? []).map((m) => ({
    id: m.organization.id,
    label: m.organization.name,
  }));
  if (organization && !items.some((it) => it.id === organization.id)) {
    items.unshift({ id: organization.id, label: organization.name });
  }
  if (items.length === 0) return null;

  const activeId = organization?.id ?? null;
  const activeLabel = organization?.name ?? items[0]?.label ?? "";
  const initials = orgInitials(activeLabel);

  if (items.length <= 1) {
    return (
      <div className="org-id-zone">
        <div className="org-id">
          <span className="org-id-badge" aria-hidden="true">
            {initials}
          </span>
          <span className="org-id-text">
            <span className="org-id-name">{activeLabel}</span>
            <span className="org-id-tag">Organization</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="org-id-zone" ref={ref}>
      <button
        type="button"
        className="org-id"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch organization"
      >
        <span className="org-id-badge" aria-hidden="true">
          {initials}
        </span>
        <span className="org-id-text">
          <span className="org-id-name">
            {busy ? "Switching…" : activeLabel}
          </span>
          <span className="org-id-tag">Organization</span>
        </span>
        <span className="org-id-chev mono">{open ? "˄" : "˅"}</span>
      </button>
      {open && (
        <div className="avmenu avmenu-down fade-in" role="menu">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              className="avmenu-item"
              disabled={busy}
              onClick={() => void choose(it.id)}
            >
              <span className="glyph mono">
                {it.id === activeId ? "●" : "○"}
              </span>
              <span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
